import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Locale } from "@/Common/Models/Localization/Locale";

interface IObstetricalFileFieldProps {
    label?: string;
    labelPrefix?: React.ReactNode;
    labelSuffix?: React.ReactNode;
    customLabel?: React.ReactNode;
    field: React.ReactNode;
    className?: string;
    unit?: React.ReactNode;
    locale?: Locale;
    labelClassName?: string;
    inlineFlex?: boolean;
    inlineFlexValue?: boolean;
    labelStyle?: React.CSSProperties;
    /**
     * If true or undefined/null, the label HTML tag will be used as a container for the field.
     * Otherwise, the field will be wrapped in a div.
     */
    useLabelHtmlTagAsContainer?: boolean;
}

abstract class BaseObstetricalFileField<TProps extends IObstetricalFileFieldProps> extends BaseReactComponent<TProps> {
    protected renderLabelContent(): JSX.Element {
        const label = this.props.label;
        const labelPrefix = this.props.labelPrefix;
        const labelSuffix = this.props.labelSuffix;
        const customLabel = this.props.customLabel;
        const labelClassName = this.props.labelClassName;

        return (
            <span
                className={`field-label ${labelClassName ?? ""} ${!label && !customLabel ? "empty-label" : ""} ${labelPrefix ? "label-with-prefix" : ""}`}
                style={this.props.labelStyle}
            >
                {labelPrefix}
                {(label || customLabel) && <span>{label ? cacheStore.GetString(label, null, this.props.locale) : customLabel}</span>}
                {labelSuffix}
            </span>
        );
    }
}

class ObstetricalFileField extends BaseObstetricalFileField<IObstetricalFileFieldProps> {
    public render(): JSX.Element {
        const inlineFlex = this.props.inlineFlex;
        const inlineFlexValue = this.props.inlineFlexValue;

        return (
            <div
                className={`field ${this.props.className ?? ""} ${this.props.unit ? "field-has-unit" : ""}`}
                style={inlineFlex ? { minHeight: 0 } : {}}
            >
                {this.renderLabelContainer([
                    this.renderLabelContent(),
                    // Need to refactor to be able to add key props.
                    // eslint-disable-next-line react/jsx-key
                    <div className={`field-value ${inlineFlex ? "width-100" : ""} ${inlineFlexValue ? "inline-flex-center" : ""}`}>
                        <div className="field-value-input">
                            {this.props.field}
                        </div>
                        {this.props.unit &&
                            <strong className="field-unit">{this.props.unit}</strong>
                        }
                    </div>
                ])}
            </div>
        );
    }

    private renderLabelContainer(childs: JSX.Element[]): JSX.Element {
        const props = {
            className: this.props.inlineFlex ? "inline-flex-field" : "",
            onClick: e => e.stopPropagation()
        };

        if (this.props.useLabelHtmlTagAsContainer === false) // strong check to enforce true by default
            return <div {...props}>{childs}</div>;

        return <label {...props}>{childs}</label>;
    }
}

interface IObstetricalFileRedirectFieldProps extends IObstetricalFileFieldProps {
    onClick: () => void;
}

class ObstetricalFileRedirectField extends BaseObstetricalFileField<IObstetricalFileRedirectFieldProps> {
    public render(): JSX.Element {
        const hasValues = !!this.props.field;
        const label = this.renderLabelContent();
        const inlineFlex = this.props.inlineFlex;

        return (
            <div
                className={`field field-redirect ${this.props.className ?? ""}`}
                style={this.props.inlineFlex ? { minHeight: 0 } : {}}
            >
                <label
                    className={inlineFlex ? "inline-flex-field" : ""}
                    onClick={e => e.stopPropagation()} /* This needs to stop event bubbling that will trigger window onClick listener because in ObstetricalFilePage component, the listener will close the clickable text popup, if the "Select from list" <a> tag is inside this <label> tag. */
                >
                    {!hasValues ? <a onClick={() => this.props.onClick()}>{label}</a> : label}
                    <div className={`field-value ${inlineFlex ? "width-100" : ""}`}>
                        <div className="field-value-input">
                            {this.props.field && <a onClick={() => this.props.onClick()}>{this.props.field}</a>}
                        </div>
                    </div>
                </label>
            </div>
        );
    }
}

class ObstetricalFileRadioButtonField extends BaseObstetricalFileField<IObstetricalFileFieldProps> {
    public render(): JSX.Element {
        return (
            <div
                className={`field ${this.props.className} ${this.props.inlineFlex ? "inline-flex-field" : ""}`}
                style={this.props.inlineFlex ? { minHeight: 0 } : {}}
            >
                <label>{this.renderLabelContent()}</label>
                <div className="field-value">
                    <div className="field-value-input">
                        {this.props.field}
                    </div>
                </div>
            </div>
        );
    }
}

export { ObstetricalFileField, ObstetricalFileRadioButtonField, ObstetricalFileRedirectField };
